// Le panier — une commande en préparation, gardée sur le poste.
//
// 🔴 UNE CLÉ PAR (STOCK, FOURNISSEUR), et les deux ont une raison MESURÉE.
//  · **par stock** : on change de stock actif, et un panier qui suivrait
//    proposerait des produits d'un autre stock — que le serveur refuserait,
//    désormais en 400 nommant les lignes. Le panier suit le périmètre ;
//  · **par fournisseur** : une commande n'a **qu'un** fournisseur. C'est une
//    contrainte du MODÈLE (`PurchaseOrder.supplier_id`, non nullable), pas un
//    choix d'interface — un panier mêlant deux fournisseurs ne pourrait jamais
//    devenir une commande.
//
// 🔴 UN PANIER EST PAR CONSTRUCTION PLUS VIEUX QUE LA BASE, ET IL NE LE SAIT
// PAS. C'est la même famille que le formulaire périmé du lot RU3-c : un état
// client qui survit à ce qu'il décrit. D'où `revaliderPanier`, appelé à la
// reprise — et qui NOMME ce qu'il retire. *Un panier qui se répare en silence
// est un panier qui ment* — c'est exactement le défaut qu'on vient de corriger
// côté serveur, on ne le réintroduit pas côté client.
//
// ⚠️ `localStorage` PEUT LEVER — navigation privée, quota, site bloqué. Chaque
// accès est protégé, et l'absence de panier est un état normal, jamais une
// erreur : le pire que puisse faire un panier indisponible est de ne pas
// exister.

import React from 'react';
import { quantiteACombler } from './reappro';

const PREFIXE = 'panier';

/** L'événement qui dit « les paniers ont changé ». `storage` ne se déclenche
 *  que dans les AUTRES onglets : sans lui, le badge de l'onglet courant ne
 *  bougerait pas — un compteur qui ignore ce qu'on vient de faire. */
export const EVENEMENT_PANIER = 'panier:maj';

const cle = (stockId, fournisseurId) => `${PREFIXE}:${stockId}:${fournisseurId}`;

function signaler() {
  try {
    window.dispatchEvent(new Event(EVENEMENT_PANIER));
  } catch {
    /* environnement sans window : rien à signaler */
  }
}

export function lirePanier(stockId, fournisseurId) {
  if (!stockId || !fournisseurId) return null;
  try {
    const brut = localStorage.getItem(cle(stockId, fournisseurId));
    if (!brut) return null;
    const panier = JSON.parse(brut);
    return Array.isArray(panier?.lignes) && panier.lignes.length ? panier : null;
  } catch {
    // Une clé illisible — JSON corrompu, format d'une version antérieure — est
    // traitée comme une absence. On ne tente pas de la réparer : un panier
    // deviné serait pire qu'un panier perdu.
    return null;
  }
}

export function ecrirePanier(stockId, fournisseurId, lignes) {
  if (!stockId || !fournisseurId) return;
  try {
    if (!lignes || lignes.length === 0) {
      localStorage.removeItem(cle(stockId, fournisseurId));
    } else {
      localStorage.setItem(cle(stockId, fournisseurId),
                           JSON.stringify({ supplier_id: fournisseurId, lignes }));
    }
  } catch {
    /* quota ou stockage refusé : le panier ne persiste pas, l'écran fonctionne */
  }
  signaler();
}

export const viderPanier = (stockId, fournisseurId) =>
  ecrirePanier(stockId, fournisseurId, []);

/** Tous les paniers DU STOCK COURANT.
 *
 *  ⚠️ FILTRÉS PAR STOCK, et ce n'est pas un détail d'implémentation : un badge
 *  qui compterait les paniers des autres stocks désignerait ce qu'on ne peut
 *  pas atteindre sans changer de périmètre. *Un compteur qui désigne ce qui
 *  n'est pas là est pire qu'un compteur absent.*
 */
export function listerPaniers(stockId) {
  if (!stockId) return [];
  const prefixe = `${PREFIXE}:${stockId}:`;
  const paniers = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(prefixe)) continue;
      const fournisseurId = k.slice(prefixe.length);
      const panier = lirePanier(stockId, fournisseurId);
      if (panier) paniers.push({ ...panier, supplier_id: fournisseurId });
    }
  } catch {
    return [];
  }
  return paniers;
}

/** Ajoute un produit au panier de SON fournisseur, ou incrémente sa ligne.
 *
 *  ⚠️ LA QUANTITÉ VIENT DE `quantiteACombler`, la même fonction que le dialogue
 *  d'ajustement, le sélecteur de réappro et le formulaire de commande. Le
 *  chariot d'une ligne produit en portait une SIXIÈME écriture —
 *  `min_stock - current_stock`, **sans le `+1`** — qui laissait le produit en
 *  alerte après réception. Elle n'a jamais été constatée parce que ce chariot
 *  était un bouton MORT : il naviguait avec des données que personne ne lisait.
 */
export function ajouterAuPanier(stockId, produit, quantiteChoisie = null) {
  if (!stockId || !produit?.supplier_id) return null;
  const fournisseurId = String(produit.supplier_id);
  const panier = lirePanier(stockId, fournisseurId);
  const lignes = panier ? [...panier.lignes] : [];
  const existante = lignes.findIndex((l) => String(l.product_id) === String(produit.id));
  // Une quantité CHOISIE l'emporte sur la quantité calculée : le sélecteur de
  // réappro laisse la corriger avant d'ajouter. En son absence, on comble.
  const quantite = quantiteChoisie || quantiteACombler(produit) || 1;

  if (existante >= 0) {
    lignes[existante] = { ...lignes[existante],
                          quantity: lignes[existante].quantity + quantite };
  } else {
    lignes.push({
      product_id: produit.id,
      product_name: produit.name,
      quantity: quantite,
      unit_price: parseFloat(produit.price) || 0,
    });
  }
  ecrirePanier(stockId, fournisseurId, lignes);
  return { supplier_id: fournisseurId, lignes };
}

//: Les raisons de retrait, NOMMÉES — reprises telles quelles du serveur, pour
//: que le client et lui parlent la même langue quand ils refusent la même ligne.
export const LIGNE_PRODUIT_INTROUVABLE = 'produit_introuvable';
export const LIGNE_AUTRE_FOURNISSEUR = 'produit_d_un_autre_fournisseur';

/**
 * Confronte un panier aux produits FRAIS du serveur.
 * Rend `{ lignes, retirees }` — et `retirees` NOMME chaque ligne écartée.
 *
 * 🔴 ON NE RÉPARE PAS EN SILENCE. Une ligne dont le produit a disparu, ou qui a
 * changé de fournisseur, est retirée **et dite**. C'est le pendant client du
 * 400 que le serveur rend désormais : les deux refusent, les deux nomment.
 * *Un panier qui se répare en silence est un panier qui ment.*
 *
 * ⚠️ ET LE PRIX N'EST PAS RAFRAÎCHI. Il a été retenu à l'ajout, et c'est le prix
 * qu'on croit payer ; le remplacer sans le dire changerait le montant sous les
 * yeux de personne. S'il a bougé, c'est une négociation, pas une correction.
 */
export function revaliderPanier(panier, produitsFrais) {
  const parId = new Map((produitsFrais || []).map((p) => [String(p.id), p]));
  const lignes = [];
  const retirees = [];
  (panier?.lignes || []).forEach((ligne) => {
    const produit = parId.get(String(ligne.product_id));
    if (!produit) {
      retirees.push({ ...ligne, raison: LIGNE_PRODUIT_INTROUVABLE });
      return;
    }
    if (String(produit.supplier_id) !== String(panier.supplier_id)) {
      retirees.push({ ...ligne, product_name: produit.name,
                      raison: LIGNE_AUTRE_FOURNISSEUR });
      return;
    }
    // Le NOM, lui, est rafraîchi : c'est un libellé, pas un engagement.
    lignes.push({ ...ligne, product_name: produit.name });
  });
  return { lignes, retirees };
}

/**
 * Le nombre de paniers du stock courant, tenu à jour.
 *
 * ⚠️ DEUX ÉVÉNEMENTS, ET LES DEUX SONT NÉCESSAIRES : `storage` ne se déclenche
 * QUE dans les autres onglets — c'est sa spécification — donc sans notre propre
 * `panier:maj`, le badge de l'onglet où l'on vient d'ajouter ne bougerait pas.
 * *Un compteur qui ignore ce que l'utilisateur vient de faire est plus troublant
 * qu'un compteur absent.*
 */
export function useNombreDePaniers(stockId) {
  const [nombre, setNombre] = React.useState(0);
  React.useEffect(() => {
    const relire = () => setNombre(listerPaniers(stockId).length);
    relire();
    window.addEventListener(EVENEMENT_PANIER, relire);
    window.addEventListener('storage', relire);
    return () => {
      window.removeEventListener(EVENEMENT_PANIER, relire);
      window.removeEventListener('storage', relire);
    };
  }, [stockId]);
  return nombre;
}
